import { useCallback, useEffect, useState } from 'react';
import type { TRPC } from '../../contexts/TRPC';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { captureAndReportError } from '../../utils/errorReporting';
import type { SuperDappDetailData } from './types';

interface UseSuperDappDetailOptions {
  dappId?: string;
  trpc: TRPC;
  onError: (message: string) => void;
}

export function useSuperDappDetail({
  dappId,
  trpc,
  onError,
}: UseSuperDappDetailOptions) {
  const [data, setData] = useState<SuperDappDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!dappId) return;

    try {
      setIsLoading(true);
      const result = await trpc.client.getSuperDappDetail.query({ dappId });

      if (isRightResult(result)) {
        setData(result.right);
      } else if (isLeftResult(result)) {
        onError(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'Failed to load super dapp detail',
        component: 'useSuperDappDetail',
      });
      onError('Failed to load dapp details');
    } finally {
      setIsLoading(false);
    }
  }, [dappId, onError, trpc]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    isLoading,
    reload: load,
  };
}
